import { useCallback, useEffect, useMemo, useState } from 'react'
import { Routes } from '@/assets/configs'
import { useRouter } from 'next/router'
import { appendParamsIfNeeded } from '@/utils'
import { getProfileAsync } from '@/modules/PetSitter/registrationServiceHelper'
import { useNotificationError } from '@/modules/Notifications'
import useCurrentUser from '@/hooks/useCurrentUser'

let manualNavigation = false // Flag to track if a manual request has been made

export const usePetSitterConnectionRoutes = ({ shouldInitialize = true } = {}) => {
  const router = useRouter()
  const [currentStep, setCurrentStep] = useState(1)
  const [addNotificationError] = useNotificationError()
  const [slugRegistration, setSlugRegistration] = useState(2)
  const { currentUnknownUser } = useCurrentUser()

  // Map registration steps to corresponding routes and steps
  const stepsConfig = useMemo(
    () => ({
      1: { route: Routes.Connection.SignUp.Details, queryParams: { type: 'pro' } },
      2: { route: Routes.Pro.SignUp.CompleteProfile, queryParams: { step: 'profile-1', type: 'pro' } },
      3: { route: Routes.Pro.SignUp.CompleteProfile, queryParams: { step: 'profile-2', type: 'pro' } },
      4: { route: Routes.Pro.SignUp.Experience, queryParams: { step: 'experience-1', type: 'pro' } },
      5: { route: Routes.Pro.SignUp.Experience, queryParams: { step: 'experience-2', type: 'pro' } },
      6: { route: Routes.Pro.SignUp.Services, queryParams: { step: 'services-1', type: 'pro' } },
      7: { route: Routes.Pro.SignUp.Services, queryParams: { step: 'services-2', type: 'pro' } },
      8: { route: Routes.Pro.SignUp.Services, queryParams: { step: 'services-3', type: 'pro' } },
      9: { route: Routes.Pro.SignUp.Services, queryParams: { step: 'services-4', type: 'pro' } },
      10: { route: Routes.Pro.SignUp.Psychology, queryParams: { step: 'psychology-1', type: 'pro' } },
      11: { route: Routes.Pro.SignUp.Knowledge, queryParams: { step: 'knowledge-1', type: 'pro' } },
      12: { route: Routes.Pro.SignUp.Identity, queryParams: { step: 'identity-1', type: 'pro' } },
      13: { route: Routes.Pro.Dashboard, queryParams: { type: 'pro' } },
    }),
    [],
  )

  const checkPetsitterConnectionRoute = (pathname = router.pathname) => {
    // Get all unique routes from stepsConfig except the last one (Dashboard)
    const registrationRoutes = new Set(
      Object.entries(stepsConfig)
        .filter(([key]) => key !== '13') // Exclude step 13
        .map(([, config]) => config.route),
    )

    return registrationRoutes.has(pathname)
  }

  // Function to handle slug-based redirection and step updating
  const handleSlugRegistration = async () => {
    if (currentUnknownUser?.['@type'] === 'ConnectedPetsitter') return
    try {
      const profileData = (await getProfileAsync()) as {
        slug: string
        isValidPsychologicalQR: boolean
        isValidKnowledgeQR: boolean
      }
      const slug = profileData.slug

      if (slug) {
        if (slug === 'petsitter_start_registering') {
          goStep(1) // Always redirect to step 1 for this specific slug
          setSlugRegistration(1)
        } else {
          const slugParts = slug.split('_')
          if (slugParts.length === 2) {
            const stepNumber = parseInt(slugParts[1], 10)
            if (!isNaN(stepNumber)) {
              // Check if trying to access step 13 and psychological QR is not valid
              if (stepNumber === 13 && (!profileData.isValidPsychologicalQR || !profileData.isValidKnowledgeQR)) {
                goStep(12) // Stay at step 12 if psychological QR is not valid
                setSlugRegistration(12)
              } else {
                goStep(stepNumber)
                setSlugRegistration(stepNumber)
              }
            } else {
              console.error('Invalid step number in slug')
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching profile data:', error)
      addNotificationError({ error })
    }
  }

  const handleManualSlugRegistration = async () => {
    if (currentUnknownUser?.['@type'] === 'ConnectedPetsitter' || router.query.step === 'identity-3') return
    try {
      const profileData = (await getProfileAsync()) as { slug: string }
      const slug = profileData.slug

      if (slug) {
        if (slug === 'petsitter_start_registering') {
          setSlugRegistration(1)
        } else {
          const slugParts = slug.split('_')
          if (slugParts.length === 2) {
            const stepNumber = parseInt(slugParts[1], 10)
            if (!isNaN(stepNumber)) {
              setSlugRegistration(stepNumber)
            } else {
              console.error('Invalid step number in slug')
            }
          }
        }
      }
    } catch (error) {
      addNotificationError({ error })
    }
  }

  // Go to the next step
  const goNextStep = () => {
    manualNavigation = true
    if (currentStep < 13) {
      const nextStep = currentStep + 1
      const nextRoute = getNextRoute(nextStep)
      router.push(nextRoute)
    }
  }

  // Go to the previous step
  const goPreviousStep = (options = { queryParams: {} }) => {
    manualNavigation = true
    if (currentStep > 1) {
      const prevStep = currentStep - 1
      const prevRoute = getNextRoute(prevStep, options.queryParams)
      router.push(prevRoute)
    }
  }

  // Add this new function before the useEffect
  const validateAndRedirectStep = async (stepNumber: number) => {
    try {
      const profileData = await getProfileAsync()

      // Validation for step 12
      if (
        stepNumber === 12 &&
        (profileData.state.isValidPsychologicalQR === false || profileData.state.isValidKnowledgeQR === false)
      ) {
        const route = getNextRoute(11)
        router.push(route)
        return 11
      }

      // Validation for step 11
      if (stepNumber === 11 && profileData.state.isValidPsychologicalQR === false) {
        const route = getNextRoute(10)
        router.push(route)
        return 10
      }

      return stepNumber
    } catch (error) {
      addNotificationError({ error })
      return stepNumber
    }
  }

  // Simplify goStep to just handle navigation
  const goStep = useCallback(
    (stepNumber) => {
      manualNavigation = true
      if (stepNumber >= 1 && stepNumber <= 13) {
        const route = getNextRoute(stepNumber)
        router.push(route)
      }
    },
    [router],
  )

  // Function to get the default step number for the current route
  const getDefaultStepForRoute = (pathname) => {
    const stepEntry = Object.entries(stepsConfig).find(([, config]) => config.route === pathname)
    return stepEntry ? parseInt(stepEntry[0]) : 1
  }

  // Extract the current step from the URL query or pathname
  const getCurrentStepFromUrl = () => {
    const stepQuery = router.query.step

    if (stepQuery) {
      const stepNumber = Object.keys(stepsConfig).find(
        (key) => stepsConfig[parseInt(key)].queryParams?.step === stepQuery,
      )
      return stepNumber ? parseInt(stepNumber) : getDefaultStepForRoute(router.pathname)
    } else {
      // No step query parameter; determine default based on route
      return getDefaultStepForRoute(router.pathname)
    }
  }

  // Initialize the current step and handle default step setting
  useEffect(() => {
    if (shouldInitialize) {
      if (
        currentUnknownUser?.['@type'] === 'ConnectedPetsitter' &&
        checkPetsitterConnectionRoute() &&
        router.query.step !== 'identity-3'
      ) {
        router.push({ pathname: Routes.Pro.Dashboard, query: { type: 'pro' } })
        return
      } else if (currentUnknownUser?.['@type'] === 'ConnectedPetsitter' || router.query.step === 'identity-3') {
        return
      }

      const stepNumber = getCurrentStepFromUrl()

      // Add validation check on mount
      validateAndRedirectStep(stepNumber).then((validatedStep) => {
        setCurrentStep(validatedStep)
      })

      if (!manualNavigation) {
        handleSlugRegistration()
      } else {
        handleManualSlugRegistration()
      }
    }
  }, [router.query, router.pathname, stepsConfig])

  // Get the next route with optional extra query parameters
  const getNextRoute = (stepNumber, extraQueryParams = {}) => {
    const stepConfig = stepsConfig[stepNumber] || stepsConfig[1]
    const { route, queryParams = {} } = stepConfig
    const combinedQueryParams = { ...queryParams, ...extraQueryParams }
    return appendParamsIfNeeded(route, combinedQueryParams)
  }

  return { goNextStep, goPreviousStep, goStep, currentStep, slugRegistration, checkPetsitterConnectionRoute }
}

export default usePetSitterConnectionRoutes
